/**
 * ResultItem2 Component Pattern
 *
 * This component follows a Container/Presenter pattern with three distinct layers:
 *
 * 1. ResultItem2 (Main API)
 *    - The public API component that parents interact with
 *    - Accepts simple, clear props that make sense to the parent context
 *    - Handles composition of the Redux wrapper and JSX components
 *
 * 2. ResultItem2ReduxWrapper (Container)
 *    - Manages all state and Redux interactions
 *    - Handles data fetching, updates, and state management
 *    - Provides data and callbacks to the JSX component
 *    - Can be replaced with different state management solutions if needed
 *
 * 3. ResultItem2Presenter (Presenter)
 *    - Pure presentational component
 *    - Contains only JSX and styling
 *    - No knowledge of Redux or state management
 *    - Easily testable with simple props
 *
 * Benefits of this pattern:
 * 1. Separation of Concerns
 *    - State management is isolated from presentation
 *    - Each layer has a single responsibility
 *    - Changes to one layer don't affect others
 *
 * 2. Testing
 *    - JSX component can be tested without Redux
 *    - Redux logic can be tested separately
 *    - Integration tests can use the main ResultItem2 component
 *
 * 3. Performance
 *    - Optional results prop for performance optimization
 *    - Prevents unnecessary Redux store lookups
 *
 * 4. Maintainability
 *    - Clear separation of concerns
 *    - Easy to understand where different types of logic live
 *    - Consistent pattern across components
 *
 * Usage:
 * ```tsx
 * <ResultItem2
 *   resultUuid="123"
 *   objectiveUuid="456"
 *   forIntervalEnding="2024-03-21"
 *   showAddButton={true}
 * />
 * ```
 */
import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../store/store';
import { updateObjective } from '../../store/map3PendingSlice';
import { ResultFlag } from '../stable/ResultFlag';
import { AddEditDeleteBar1 } from '../stable/AddEditDeleteBar1';
import { getShortIdFromUuid } from '@mm-shared/utils/getShortIdFromUuid';
import { createResult1 } from '@mm-shared/types/Result1.utils';
import { useCurrentMap } from '../../hooks/useCurrentMap';
// import { selectUserRoleForMap } from '../../store/selectors';
import { useRoleWithOverride } from '../../hooks/useRoleWithOverride';
import { getPermissionsForRole } from '@mm-shared/authz';

// Simple props interface for the main component
interface ResultItem2Props {
  resultUuid: string;
  objectiveUuid: string;
  /**
   * The canonical deadline for this result.
   * This is the primary field used for result placement and filtering.
   */
  deadline: string;
  /**
   * @deprecated Use deadline instead. This field is maintained for backward compatibility.
   * Will be removed in a future version.
   */
  forIntervalEnding?: string;
  showAddButton?: boolean;
}

// The main component that parents will use - simple API
export function ResultItem2(props: ResultItem2Props) {
  // Create empty default props
  const emptyPresenterProps: ResultItem2PresenterProps = {
    text: '',
    isKeyResult: false,
    delivered: false,
    isEditing: false,
    editingText: '',
    isActionBarVisible: false,
    onDeliveredChange: () => {},
    onTextChange: () => {},
    onTextSave: () => {},
    onTextCancel: () => {},
    onDelete: () => {},
    onFlag: () => {},
    onEdit: () => {},
    onAddAfter: undefined,
    showAddButton: false,
    onMouseEnter: () => {},
    onMouseLeave: () => {},
    onKeyDown: () => {},
    onFocus: () => {},
    onBlur: (e: React.FocusEvent) => {},
  };

  return (
    <ResultItem2ReduxWrapper {...props}>
      <ResultItem2Presenter {...emptyPresenterProps} />
    </ResultItem2ReduxWrapper>
  );
}

// Internal props for the presenter component
interface ResultItem2PresenterProps {
  text: string;
  isKeyResult: boolean;
  delivered: boolean;
  isEditing: boolean;
  editingText: string;
  isActionBarVisible: boolean;
  onDeliveredChange: (delivered: boolean) => void;
  onTextChange: (text: string) => void;
  onTextSave: () => void;
  onTextCancel: () => void;
  onDelete: () => void;
  onFlag: () => void;
  onEdit: () => void;
  onAddAfter?: () => void;
  showAddButton?: boolean;
  canEdit?: boolean;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onKeyDown: (e: React.KeyboardEvent) => void;
  onFocus: () => void;
  onBlur: (e: React.FocusEvent) => void;
}

// The Redux wrapper that handles all state management
function ResultItem2ReduxWrapper({
  resultUuid,
  objectiveUuid,
  deadline,
  forIntervalEnding,
  showAddButton = false,
  children,
}: ResultItem2Props & { children: React.ReactElement<ResultItem2PresenterProps> }) {
  const dispatch = useDispatch<AppDispatch>();

  // Local UI state
  const [isEditing, setIsEditing] = useState(false);
  const [editingText, setEditingText] = useState('');
  const [isActionBarVisible, setIsActionBarVisible] = useState(false);
  const [isKeyboardNavigating, setIsKeyboardNavigating] = useState(false);

  // Get objective directly from Redux store
  const objective = useSelector((state: RootState) =>
    state.map3Data.objectives.find((obj) => obj.uuid === objectiveUuid),
  );

  const result = objective ? objective.results.find((r) => r.uuid === resultUuid) : undefined;

  // Check user's role for edit permissions
  const currentMap = useCurrentMap();
  // const userRole = useSelector((state: RootState) =>
  //   currentMap ? selectUserRoleForMap(state, currentMap.uuid) : null,
  // );
  const userRole = useRoleWithOverride(currentMap?.uuid || '');
  const { canEdit } = getPermissionsForRole(userRole);

  if (!objective) {
    console.error(`Objective ${objectiveUuid} not found`);
    return null;
  }

  // Return null silently instead of throwing an error
  if (!result) {
    return null;
  }

  // All handlers defined in one place
  const handleDeliveredChange = (delivered: boolean) => {
    if (!canEdit) {
      console.warn('Result delivery change denied: user does not have edit permissions');
      return;
    }

    dispatch(
      updateObjective({
        uuid: objectiveUuid,
        updates: {
          results: objective.results.map((r) =>
            r.uuid === resultUuid ? createResult1({ ...r, delivered }) : r,
          ),
        },
      }),
    );
  };

  const handleTextSave = () => {
    if (editingText.trim() === result.text) {
      setIsEditing(false);
      return;
    }

    if (!canEdit) {
      console.warn('Result text edit denied: user does not have edit permissions');
      setIsEditing(false);
      return;
    }

    dispatch(
      updateObjective({
        uuid: objectiveUuid,
        updates: {
          results: objective.results.map((r) =>
            r.uuid === resultUuid ? createResult1({ ...r, text: editingText.trim() }) : r,
          ),
        },
      }),
    );
    setIsEditing(false);
  };

  const handleDelete = () => {
    if (!canEdit) {
      console.warn('Result delete denied: user does not have edit permissions');
      return;
    }

    dispatch(
      updateObjective({
        uuid: objectiveUuid,
        updates: {
          results: objective.results.filter((r) => r.uuid !== resultUuid),
        },
      }),
    );
  };

  const handleFlag = () => {
    if (!canEdit) {
      console.warn('Result flag toggle denied: user does not have edit permissions');
      return;
    }

    dispatch(
      updateObjective({
        uuid: objectiveUuid,
        updates: {
          results: objective.results.map((r) =>
            r.uuid === resultUuid ? createResult1({ ...r, isKeyResult: !r.isKeyResult }) : r,
          ),
        },
      }),
    );
  };

  const handleAddAfter = () => {
    if (!canEdit) {
      console.warn('Result add denied: user does not have edit permissions');
      return;
    }

    const uuid = crypto.randomUUID();
    const newResult = createResult1({
      uuid: uuid,
      shortId: getShortIdFromUuid(uuid),
      text: '',
      delivered: false,
      isKeyResult: false,
      deadline: deadline,
      createdDate: new Date().toISOString(),
    });

    // Find the index of the current result and insert after it
    const currentIndex = objective.results.findIndex((r) => r.uuid === resultUuid);
    const newResults = [
      ...objective.results.slice(0, currentIndex + 1),
      newResult,
      ...objective.results.slice(currentIndex + 1),
    ];

    dispatch(
      updateObjective({
        uuid: objectiveUuid,
        updates: {
          results: newResults,
        },
      }),
    );

    // Immediately set the new result to edit mode
    setEditingText('');
    setIsEditing(true);
  };

  // Simple keyboard navigation
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (isEditing) return; // Don't handle navigation while editing

    switch (e.key) {
      case 'Enter':
      case ' ':
        // Only handle Enter/Space if focused on main container or checkbox
        const focusedElement = document.activeElement;
        const isOnMainContainer = focusedElement === e.currentTarget;
        const isOnCheckbox =
          focusedElement?.tagName === 'INPUT' && focusedElement.getAttribute('type') === 'checkbox';

        if (isOnMainContainer || isOnCheckbox) {
          e.preventDefault();
          // Toggle checkbox (primary action)
          handleDeliveredChange(!result.delivered);
        }
        // Let action buttons handle their own Enter/Space events
        break;

      case 'ArrowLeft':
      case 'ArrowRight':
        e.preventDefault();

        // Enable keyboard navigation mode and ensure action bar is visible
        setIsKeyboardNavigating(true);
        setIsActionBarVisible(true);

        // Navigate within the item controls
        const currentTarget = e.currentTarget as HTMLElement;
        const checkbox = currentTarget.querySelector('input[type="checkbox"]') as HTMLInputElement;

        // Get all visible action bar buttons
        const actionBar = currentTarget.querySelector('[role="toolbar"]') as HTMLElement;
        const buttons = actionBar
          ? (Array.from(actionBar.querySelectorAll('button')) as HTMLButtonElement[])
          : [];

        // Always include all buttons since we're keeping action bar visible
        const allControls: HTMLElement[] = [checkbox, ...buttons].filter(Boolean);

        if (allControls.length === 0) {
          return;
        }

        let currentIndex = allControls.indexOf(document.activeElement as HTMLElement);

        // If main container is focused (not an internal element), choose start position based on arrow direction
        if (currentIndex === -1) {
          if (e.key === 'ArrowRight') {
            // Start from beginning (checkbox)
            currentIndex = -1; // Will become 0 after increment
          } else {
            // Start from end
            currentIndex = allControls.length; // Will become length-1 after decrement
          }
        }

        let nextIndex;

        if (e.key === 'ArrowRight') {
          // Move right, stop at last element
          nextIndex = Math.min(currentIndex + 1, allControls.length - 1);
        } else {
          // Move left, stop at first element (checkbox)
          nextIndex = Math.max(currentIndex - 1, 0);
        }

        // Focus the target element
        const targetElement = allControls[nextIndex];
        if (targetElement) {
          targetElement.focus();
        }
        break;
    }
  };

  const handleFocus = () => {
    if (!isEditing) {
      setIsActionBarVisible(true);
      setIsKeyboardNavigating(true);
    }
  };

  const handleBlur = (e: React.FocusEvent) => {
    // Check if focus is moving to an element outside this result item
    const currentTarget = e.currentTarget;
    const relatedTarget = e.relatedTarget as HTMLElement;

    // If relatedTarget is null (focus leaving page) or not contained within currentTarget,
    // then focus is leaving this result item entirely
    const isFocusLeavingComponent = !relatedTarget || !currentTarget.contains(relatedTarget);

    if (isFocusLeavingComponent) {
      // Focus is leaving the entire result item component
      setIsActionBarVisible(false);
      setIsKeyboardNavigating(false);
    }

    // If focus is staying within the component (e.g., moving from checkbox to action bar button),
    // keep the current state - don't hide the action bar during internal navigation
  };

  const handleMouseEnter = () => {
    if (!isEditing && !isKeyboardNavigating) {
      setIsActionBarVisible(true);
    }
  };

  const handleMouseLeave = () => {
    if (!isKeyboardNavigating) {
      setIsActionBarVisible(false);
    }
  };

  const presenterProps: ResultItem2PresenterProps = {
    text: result.text,
    isKeyResult: result.isKeyResult,
    delivered: result.delivered,
    isEditing,
    editingText,
    isActionBarVisible,
    onDeliveredChange: handleDeliveredChange,
    onTextChange: setEditingText,
    onTextSave: handleTextSave,
    onTextCancel: () => {
      setIsEditing(false);
      setEditingText(result.text);
    },
    onDelete: handleDelete,
    onFlag: handleFlag,
    onEdit: () => {
      if (!canEdit) {
        console.warn('Result edit denied: user does not have edit permissions');
        return;
      }
      setEditingText(result.text);
      setIsEditing(true);
    },
    onAddAfter: showAddButton ? handleAddAfter : undefined,
    showAddButton,
    canEdit,
    onMouseEnter: handleMouseEnter,
    onMouseLeave: handleMouseLeave,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
    onBlur: handleBlur,
  };

  return React.cloneElement(children, presenterProps);
}

// Pure presentational component
function ResultItem2Presenter({
  text,
  isKeyResult,
  delivered,
  isEditing,
  editingText,
  isActionBarVisible,
  onDeliveredChange,
  onTextChange,
  onTextSave,
  onTextCancel,
  onDelete,
  onFlag,
  onEdit,
  onAddAfter,
  showAddButton = false,
  canEdit = true,
  onMouseEnter,
  onMouseLeave,
  onKeyDown,
  onFocus,
  onBlur,
}: ResultItem2PresenterProps) {
  return (
    <div
      className="flex items-center p-1 flex-1 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1 rounded"
      tabIndex={0}
      onKeyDown={onKeyDown}
      onFocus={onFocus}
      onBlur={onBlur}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role="listitem"
      aria-label={`Result: ${text}${isKeyResult ? ' (key result)' : ''}${delivered ? ' (completed)' : ''}`}
    >
      <input
        type="checkbox"
        checked={delivered}
        onChange={(e) => onDeliveredChange(e.target.checked)}
        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-600 mr-2"
        tabIndex={-1}
        aria-label={delivered ? 'Mark as incomplete' : 'Mark as complete'}
      />
      {isEditing ? (
        <input
          type="text"
          value={editingText}
          onChange={(e) => onTextChange(e.target.value)}
          onBlur={onTextSave}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              onTextSave();
            } else if (e.key === 'Escape') {
              onTextCancel();
            }
          }}
          className="flex-1 px-2 py-1 rounded border border-gray-300 dark:bg-zinc-700 dark:text-white"
          autoFocus
        />
      ) : (
        <div className={`flex-1 cursor-pointer relative ${isKeyResult ? 'font-bold' : ''}`}>
          <div className="pr-20 flex items-center gap-2">
            <ResultFlag isKeyResult={isKeyResult} />
            <span>{text}</span>
          </div>
          <div className="absolute right-0 top-1/2 -translate-y-1/2">
            <AddEditDeleteBar1
              isVisible={isActionBarVisible}
              // showAdd is commented out due to a bug where adding a new result can erase the current result's value.
              // Be sure to test for and address the issue if/when you uncomment this.
              // showAdd={showAddButton}
              showAdd={false}
              // onAdd={onAddAfter}
              showEdit={canEdit}
              showDelete={canEdit}
              showFlag={canEdit}
              isFlagged={isKeyResult}
              onEdit={onEdit}
              onDelete={onDelete}
              onFlag={onFlag}
            />
          </div>
        </div>
      )}
    </div>
  );
}
